import {
  faCircleCheck,
  faCircleExclamation,
  faInfoCircle,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';

import './Alert.css';

import { ReactNode, useEffect, useState } from 'react';

import { Icon } from '../icon/Icon';

type AlertActionButton = {
  label: string;
  onClick: () => void;
};

type AlertProps = {
  actionButtons?: AlertActionButton[];
  id: string;
  isVisible: boolean;
  isAssessive?: boolean;
  message: ReactNode | string;
  title?: {
    text: string;
    showTitle: boolean;
  };
  type: 'error' | 'warning' | 'info' | 'success';
  variant?: 'default' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  isRoleAlert?: boolean;
  dataTestId?: string;
} & React.RefAttributes<HTMLDivElement>;

export function Alert({
  actionButtons,
  id,
  isVisible,
  isAssessive,
  isRoleAlert,
  message,
  title,
  type,
  variant = 'default',
  size = 'lg',
  dataTestId,
}: AlertProps) {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(isVisible);
  }, [isVisible]);

  const icon = {
    error: faCircleExclamation,
    warning: faTriangleExclamation,
    info: faInfoCircle,
    success: faCircleCheck,
  };

  const role = (() => {
    return (isRoleAlert && isActive) || isAssessive ? 'alert' : undefined;
  })();

  return (
    <div
      id={id}
      className={`alert ${type} ${isActive ? 'active' : 'idle'} ${variant} ${size}`}
      aria-hidden={!isActive}
      aria-live={isAssessive ? 'assertive' : 'polite'}
      role={role}
      tabIndex={isAssessive ? -1 : undefined}
      data-testid={dataTestId}
    >
      {variant === 'default' && <Icon icon={{ faIconDefinition: icon[type] }} />}
      <div className="content">
        <p
          className={`title ${title?.showTitle ? 'show' : 'hide'}`}
          id={`${id}-title`}
          aria-hidden={!title?.showTitle}
        >
          {title?.text}
        </p>
        {typeof message === 'string' ? (
          <p className="text" id={`${id}-message`}>
            {message}
          </p>
        ) : (
          message
        )}
      </div>
    </div>
  );
}
